import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import {
  Building2,
  Check,
  Copy,
  KeyRound,
  Mail,
  Plus,
  Search,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  UserPlus,
} from 'lucide-react';
import {
  USER_ROLE_HINT,
  USER_ROLE_LABEL,
  USER_STATUS_LABEL,
  UserRole,
  isWelozeRole,
  type CreateUserResponseDto,
  type SupplierListResponseDto,
  type SupplierStoreListItemDto,
  type UserDetailDto,
  type UserInviteDto,
  type UserListItemDto,
  type UserStatus,
} from '@weloze/shared';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDateTime } from '../../lib/format';
import { useTableSort } from '../../lib/sorting';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
  SortHeader,
} from '../../components/ui';

/**
 * `/admin/usuarios` — quem entra no portal (PLAN §7, tela 11).
 *
 * A tela que faltava. De (a) a (k) o portal inteiro foi construído sem ela: o
 * único usuário do banco era o do seed, e nenhum fornecedor tinha como entrar
 * para tratar um 8D ou mandar um certificado.
 *
 * ## Não existe campo de senha aqui, e não pode passar a existir
 *
 * Quem cria a conta emite um **convite**; quem escolhe a senha é o dono dela.
 * Se o administrador digitasse a senha de outra pessoa, toda linha de
 * `audit_log` assinada por aquele usuário passaria a ter dois autores possíveis
 * — e a trilha existe justamente para responder "quem fez isto".
 *
 * ## O link aparece quando o e-mail não sai
 *
 * Com `MAIL_DRY_RUN=true`, que é o padrão, nada é enviado. Sem mostrar o link
 * nesse caso, a tela criaria contas que ninguém consegue ativar. Quando o Graph
 * estiver ligado e o e-mail sair de verdade, o link **não** é exibido: o segredo
 * fica na caixa de entrada do dono, e não numa segunda tela.
 */

const COLUNAS = {
  nome: (u: UserListItemDto) => u.name,
  papel: (u: UserListItemDto) => USER_ROLE_LABEL[u.role],
  lojas: (u: UserListItemDto) => u.storeCount,
  situacao: (u: UserListItemDto) => u.status,
  acesso: (u: UserListItemDto) => u.lastLoginAt,
};

export function UsersPage() {
  const { user: eu } = useAuth();

  const [items, setItems] = useState<UserListItemDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [papel, setPapel] = useState('');
  const [situacao, setSituacao] = useState('');
  const [criando, setCriando] = useState(false);
  const [abrindo, setAbrindo] = useState<string | null>(null);
  const [novo, setNovo] = useState<CreateUserResponseDto | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setItems(await api.get<UserListItemDto[]>('/users'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível carregar os usuários.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Filtrados no navegador: a lista inteira cabe numa requisição — são dezenas
  // de contas, não milhares — e mandar cada tecla ao servidor seria uma ida ao
  // banco para não mudar nada.
  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return (items ?? []).filter((u) => {
      if (papel && u.role !== papel) return false;
      if (situacao && u.status !== situacao) return false;
      if (!termo) return true;
      return `${u.name} ${u.email}`.toLowerCase().includes(termo);
    });
  }, [items, busca, papel, situacao]);

  const { rows, sort, toggle } = useTableSort(visiveis, COLUNAS, {
    key: 'nome',
    direction: 'asc',
  });

  const semPrimeiroAcesso = (items ?? []).filter(
    (u) => u.pendingFirstAccess && u.status === 'ACTIVE',
  );

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Usuários</h1>
          <p className="mt-1 text-sm text-slate-600">
            Quem entra no portal, e o que cada um enxerga. Ninguém define a senha de outra pessoa:
            criar um usuário envia um convite para ele escolher a dele.
          </p>
        </div>
        <Button onClick={() => setCriando(true)}>
          <UserPlus className="size-4" aria-hidden />
          Novo usuário
        </Button>
      </header>

      {error && <Alert>{error}</Alert>}

      {semPrimeiroAcesso.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/40">
          <div className="flex items-center gap-2">
            <ShieldAlert className="size-4 text-amber-600" aria-hidden />
            <h2 className="font-medium text-slate-900">
              {semPrimeiroAcesso.length} conta{semPrimeiroAcesso.length === 1 ? '' : 's'} sem
              primeiro acesso
            </h2>
          </div>
          <p className="mt-1 text-sm text-slate-600">
            O convite foi emitido e ninguém definiu a senha ainda. Enquanto isso, a conta existe e
            não entra. Abra o usuário para reenviar o convite.
          </p>
        </Card>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <Field
          label="Buscar"
          name="busca"
          className="w-64"
          icon={<Search className="size-4" aria-hidden />}
          placeholder="Nome ou e-mail"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <Select
          label="Papel"
          name="papel"
          className="w-52"
          value={papel}
          onChange={(e) => setPapel(e.target.value)}
        >
          <option value="">Todos</option>
          {(Object.keys(USER_ROLE_LABEL) as UserRole[]).map((valor) => (
            <option key={valor} value={valor}>
              {USER_ROLE_LABEL[valor]}
            </option>
          ))}
        </Select>
        <Select
          label="Situação"
          name="situacao"
          className="w-44"
          value={situacao}
          onChange={(e) => setSituacao(e.target.value)}
        >
          <option value="">Todas</option>
          {(['ACTIVE', 'INACTIVE', 'ANONYMIZED'] as UserStatus[]).map((valor) => (
            <option key={valor} value={valor}>
              {USER_STATUS_LABEL[valor]}
            </option>
          ))}
        </Select>
      </div>

      {!items ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-14" />
          ))}
        </div>
      ) : visiveis.length === 0 ? (
        <EmptyState
          title="Nenhum usuário nesta situação"
          description="Ajuste os filtros, ou crie o primeiro usuário desta carteira."
          action={<Button onClick={() => setCriando(true)}>Novo usuário</Button>}
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full min-w-[52rem] text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <SortHeader column="nome" sort={sort} onSort={toggle}>
                  Usuário
                </SortHeader>
                <SortHeader column="papel" sort={sort} onSort={toggle}>
                  Papel
                </SortHeader>
                <SortHeader column="lojas" sort={sort} onSort={toggle}>
                  Lojas
                </SortHeader>
                <SortHeader column="situacao" sort={sort} onSort={toggle}>
                  Situação
                </SortHeader>
                <SortHeader column="acesso" sort={sort} onSort={toggle}>
                  Último acesso
                </SortHeader>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((u) => (
                <tr key={u.id} className="transition-colors hover:bg-slate-50/70">
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => setAbrindo(u.id)}
                      className="text-left font-medium text-brand-700 underline-offset-4 hover:underline"
                    >
                      {u.name}
                    </button>
                    <p className="text-xs text-slate-500">{u.email}</p>
                    {u.id === eu?.id && (
                      <span className="text-xs text-slate-400">é você</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {USER_ROLE_LABEL[u.role]}
                    {u.isMaster && (
                      <Badge tone="accent" className="ml-2">
                        master
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {isWelozeRole(u.role) ? (
                      <span className="text-slate-500">carteira inteira</span>
                    ) : u.storeCount === 0 ? (
                      // Vermelho só em conta ativa: num usuário desativado o
                      // problema é a desativação, e o selo diria em vermelho
                      // algo que ninguém precisa resolver.
                      u.status === 'ACTIVE' ? (
                        <Badge tone="danger">nenhuma</Badge>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )
                    ) : (
                      `${u.storeCount} loja${u.storeCount === 1 ? '' : 's'}`
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <SituacaoBadge user={u} />
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {u.lastLoginAt ? formatDateTime(u.lastLoginAt) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateModal
        open={criando}
        onClose={() => setCriando(false)}
        onDone={(resposta) => {
          setCriando(false);
          setNovo(resposta);
          void load();
        }}
      />

      <Modal
        open={novo !== null}
        onClose={() => setNovo(null)}
        size="lg"
        title="Usuário criado"
        description={novo ? `${novo.user.name} · ${novo.user.email}` : undefined}
      >
        {novo && (
          <div className="space-y-4">
            <InvitePanel invite={novo.invite} />
            <div className="flex justify-end">
              <Button onClick={() => setNovo(null)}>Fechar</Button>
            </div>
          </div>
        )}
      </Modal>

      <UserModal
        userId={abrindo}
        onClose={() => setAbrindo(null)}
        onChanged={() => void load()}
      />
    </div>
  );
}

/**
 * A situação, em três estados e não em dois.
 *
 * "Desativado" e "nunca entrou" são coisas diferentes e exigem ações
 * diferentes: uma se resolve reativando, a outra reenviando o convite. Um
 * booleano "ativo" esconderia justamente a conta que parece pronta e não é.
 */
function SituacaoBadge({ user }: { user: UserListItemDto }) {
  if (user.status === 'ANONYMIZED') return <Badge>Anonimizado</Badge>;
  if (user.status === 'INACTIVE') return <Badge tone="danger">Desativado</Badge>;
  if (user.pendingFirstAccess) return <Badge tone="warning">Aguardando primeiro acesso</Badge>;
  return <Badge tone="success">Ativo</Badge>;
}

/** O resultado do convite, com o link só quando o e-mail não saiu. */
function InvitePanel({ invite }: { invite: UserInviteDto }) {
  const [copiado, setCopiado] = useState(false);

  async function copiar() {
    if (!invite.link) return;
    try {
      await navigator.clipboard.writeText(invite.link);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 2000);
    } catch {
      // Navegador sem permissão de área de transferência: o link está à vista
      // no campo abaixo, que continua selecionável.
      setCopiado(false);
    }
  }

  /*
   * Quem decide é o `link`, e não o `mailStatus`.
   *
   * O servidor só devolve o link quando a mensagem **não chegou ao dono da
   * conta** — e `SENT` com `MAIL_RECIPIENT_OVERRIDE` ativo é justamente um caso
   * em que ela não chegou. Olhar o status aqui faria a tela dizer "enviado" para
   * um e-mail que foi inteiro para o endereço de teste.
   */
  if (invite.link === null) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 text-sm text-slate-700">
        <p className="flex items-center gap-2 font-medium text-emerald-800">
          <Mail className="size-4" aria-hidden />
          Convite enviado por e-mail.
        </p>
        <p className="mt-1">
          A pessoa tem até {formatDateTime(invite.expiresAt)} para definir a senha. O link vale uma
          única vez e não é exibido aqui — ele está na caixa de entrada dela.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/50 p-3 text-sm text-slate-700">
      <p className="font-medium text-amber-900">
        {invite.mailStatus === 'DRY_RUN'
          ? 'O e-mail não foi enviado: o portal está em modo de ensaio (MAIL_DRY_RUN).'
          : invite.mailStatus === 'SENT'
            ? 'O e-mail saiu, mas foi desviado para o endereço de teste (MAIL_RECIPIENT_OVERRIDE) — quem deveria receber não recebeu.'
            : 'O e-mail falhou.'}
      </p>
      {invite.mailError && <p className="text-xs text-slate-600">{invite.mailError}</p>}
      <p>
        Entregue este link à pessoa por um canal em que você confie. Ele vale{' '}
        <strong>uma única vez</strong> e expira em {formatDateTime(invite.expiresAt)}.
      </p>
      {invite.link && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            readOnly
            value={invite.link}
            onFocus={(e) => e.currentTarget.select()}
            className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-xs text-slate-700"
          />
          <Button type="button" variant="outline" size="sm" onClick={() => void copiar()}>
            {copiado ? <Check className="size-4" aria-hidden /> : <Copy className="size-4" aria-hidden />}
            {copiado ? 'Copiado' : 'Copiar'}
          </Button>
        </div>
      )}
      <p className="text-xs text-slate-600">
        Esta é a única vez que o link aparece. Se fechar sem copiar, use “Reenviar convite” na ficha
        do usuário — o antigo deixa de valer e outro é emitido.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Criação
// ---------------------------------------------------------------------------

function CreateModal({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: (resposta: CreateUserResponseDto) => void;
}) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    role: UserRole.SUPPLIER_USER as UserRole,
    storeIds: [] as string[],
  });
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  // Estado limpo a cada abertura: sem isto, o segundo usuário criado herdaria as
  // lojas do primeiro — e conceder loja errada é o erro mais caro desta tela.
  useEffect(() => {
    if (!open) return;
    setForm({ name: '', email: '', role: UserRole.SUPPLIER_USER, storeIds: [] });
    setFields({});
    setError(null);
  }, [open]);

  const precisaLoja = !isWelozeRole(form.role);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSalvando(true);
    setError(null);
    setFields({});
    try {
      const resposta = await api.post<CreateUserResponseDto>('/users', {
        name: form.name,
        email: form.email,
        role: form.role,
        storeIds: precisaLoja ? form.storeIds : [],
      });
      onDone(resposta);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.fields ? null : err.message);
        setFields(err.fields ?? {});
      } else {
        setError('Não foi possível criar o usuário.');
      }
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Novo usuário"
      description="A senha é escolhida por quem vai usar a conta, pelo link do convite."
    >
      <form onSubmit={submit} className="space-y-4">
        {error && <Alert>{error}</Alert>}

        <div className="flex flex-wrap gap-3">
          <Field
            label="Nome"
            name="name"
            className="min-w-[14rem] flex-1"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            error={fields.name}
          />
          <Field
            label="E-mail"
            name="email"
            type="email"
            className="min-w-[14rem] flex-1"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            error={fields.email}
            hint="É por ele que a pessoa entra e recebe o convite."
          />
        </div>

        <Select
          label="Papel"
          name="role"
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value as UserRole })}
          error={fields.role}
          hint={USER_ROLE_HINT[form.role]}
        >
          {(Object.keys(USER_ROLE_LABEL) as UserRole[]).map((valor) => (
            <option key={valor} value={valor}>
              {USER_ROLE_LABEL[valor]}
            </option>
          ))}
        </Select>

        {precisaLoja ? (
          <StorePicker
            selected={form.storeIds}
            onChange={(storeIds) => setForm({ ...form, storeIds })}
            error={fields.storeIds}
          />
        ) : (
          <p className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 text-sm text-slate-600">
            Usuário Weloze enxerga a <strong>carteira inteira</strong> sem concessão por loja. Só o
            administrador altera dados; o perfil de consulta lê tudo e não muda nada.
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={salvando}>
            Criar e enviar convite
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Escolha das lojas, no próprio formulário de criação (decisão do Vinicius).
 *
 * Só lojas **monitoradas** aparecem: desligar o monitoramento revoga o acesso,
 * então conceder acesso a loja não monitorada criaria uma linha que a próxima
 * operação apagaria. O servidor recusa de qualquer jeito — aqui a lista nem
 * oferece, para o erro não chegar a ser cometido.
 */
function StorePicker({
  selected,
  onChange,
  error,
  excluded = [],
}: {
  selected: string[];
  onChange: (storeIds: string[]) => void;
  error?: string | undefined;
  /** Lojas que o usuário já tem — some da lista em vez de aparecer sem efeito. */
  excluded?: string[];
}) {
  const [lojas, setLojas] = useState<SupplierStoreListItemDto[] | null>(null);
  const [busca, setBusca] = useState('');
  const [falha, setFalha] = useState<string | null>(null);

  useEffect(() => {
    let ativo = true;
    api
      .get<SupplierListResponseDto>('/suppliers?scope=monitored')
      .then((resposta) => {
        if (ativo) setLojas(resposta.items);
      })
      .catch((err: unknown) => {
        if (ativo) {
          setFalha(
            err instanceof ApiError ? err.message : 'Não foi possível carregar as lojas.',
          );
        }
      });
    return () => {
      ativo = false;
    };
  }, []);

  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return (lojas ?? [])
      .filter((loja) => !excluded.includes(loja.storeId))
      .filter((loja) => {
        if (!termo) return true;
        return `${loja.companyName} ${loja.storeName} ${loja.protheusRef} ${loja.city ?? ''}`
          .toLowerCase()
          .includes(termo);
      });
  }, [lojas, busca, excluded]);

  function alterna(storeId: string) {
    onChange(
      selected.includes(storeId)
        ? selected.filter((id) => id !== storeId)
        : [...selected, storeId],
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <p className="text-sm font-medium text-slate-700">
          Lojas que este usuário enxerga
          {selected.length > 0 && (
            <span className="ml-2 font-normal text-slate-500">
              {selected.length} selecionada{selected.length === 1 ? '' : 's'}
            </span>
          )}
        </p>
        {selected.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-xs text-slate-500 underline-offset-4 hover:underline"
          >
            Limpar seleção
          </button>
        )}
      </div>

      {falha && <Alert>{falha}</Alert>}

      <Field
        label="Buscar loja"
        name="buscaLoja"
        icon={<Search className="size-4" aria-hidden />}
        placeholder="Nome, código ou cidade"
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
      />

      <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200">
        {!lojas ? (
          <div className="space-y-1 p-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : visiveis.length === 0 ? (
          <p className="p-4 text-sm text-slate-500">
            Nenhuma loja monitorada corresponde à busca.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {visiveis.map((loja) => {
              const marcada = selected.includes(loja.storeId);
              return (
                <li key={loja.storeId}>
                  <label className="flex cursor-pointer items-start gap-3 px-3 py-2 transition-colors hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={marcada}
                      onChange={() => alterna(loja.storeId)}
                      className="mt-1 size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-slate-800">
                        {loja.companyName}
                      </span>
                      <span className="block truncate text-xs text-slate-500">
                        {loja.storeName}
                        {loja.city && ` · ${loja.city}/${loja.state}`}
                      </span>
                    </span>
                    <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600">
                      {loja.protheusRef}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {error && <p className="text-xs font-medium text-red-600">{error}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ficha do usuário
// ---------------------------------------------------------------------------

function UserModal({
  userId,
  onClose,
  onChanged,
}: {
  userId: string | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { user: eu } = useAuth();
  const { ask, dialog } = useConfirm();

  const [user, setUser] = useState<UserDetailDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [convite, setConvite] = useState<UserInviteDto | null>(null);
  const [concedendo, setConcedendo] = useState(false);
  const [novasLojas, setNovasLojas] = useState<string[]>([]);
  const [ocupado, setOcupado] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    setError(null);
    try {
      setUser(await api.get<UserDetailDto>(`/users/${userId}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível carregar o usuário.');
    }
  }, [userId]);

  useEffect(() => {
    setUser(null);
    setConvite(null);
    setAviso(null);
    setConcedendo(false);
    setNovasLojas([]);
    void load();
  }, [load]);

  const souEu = user?.id === eu?.id;

  async function acao(fn: () => Promise<unknown>, mensagem: string) {
    setOcupado(true);
    setError(null);
    setAviso(null);
    try {
      await fn();
      setAviso(mensagem);
      await load();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível concluir a ação.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <>
      <Modal
        open={userId !== null}
        onClose={onClose}
        size="lg"
        title={user?.name ?? 'Usuário'}
        description={user?.email}
      >
        {!user ? (
          <div className="space-y-2">
            {error && <Alert>{error}</Alert>}
            {!error && [0, 1, 2].map((i) => <Skeleton key={i} className="h-12" />)}
          </div>
        ) : (
          <div className="space-y-5">
            {error && <Alert>{error}</Alert>}
            {aviso && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-sm text-emerald-800">
                {aviso}
              </div>
            )}

            <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
              <Linha label="Situação">
                <SituacaoBadge user={user} />
              </Linha>
              <Linha label="Último acesso">
                {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'Nunca entrou'}
              </Linha>
              <Linha label="Criado em">{formatDateTime(user.createdAt)}</Linha>
              <Linha label="Convite em aberto">
                {user.inviteExpiresAt ? `até ${formatDateTime(user.inviteExpiresAt)}` : '—'}
              </Linha>
            </dl>

            {/* Papel e situação: os dois campos que mexem no que a pessoa pode
                fazer, e por isso os dois que ninguém altera em si mesmo. */}
            <div className="flex flex-wrap gap-3">
              <Select
                label="Papel"
                name="role"
                className="min-w-[13rem] flex-1"
                value={user.role}
                disabled={souEu || ocupado || user.status === 'ANONYMIZED'}
                onChange={(e) =>
                  void acao(
                    () => api.put(`/users/${user.id}`, { role: e.target.value }),
                    'Papel alterado.',
                  )
                }
                hint={souEu ? 'Você não altera o próprio papel.' : USER_ROLE_HINT[user.role]}
              >
                {(Object.keys(USER_ROLE_LABEL) as UserRole[]).map((valor) => (
                  <option key={valor} value={valor}>
                    {USER_ROLE_LABEL[valor]}
                  </option>
                ))}
              </Select>

              <div className="flex min-w-[13rem] flex-1 items-end">
                {user.status === 'ACTIVE' ? (
                  <Button
                    variant="outline"
                    disabled={souEu || ocupado}
                    onClick={() =>
                      ask({
                        title: 'Desativar usuário',
                        body: (
                          <>
                            <p>
                              <strong>{user.name}</strong> deixa de entrar no portal{' '}
                              <strong>imediatamente</strong> — as sessões abertas caem no próximo
                              clique.
                            </p>
                            <p>
                              As concessões de loja ficam guardadas: reativar devolve o acesso como
                              estava.
                            </p>
                          </>
                        ),
                        confirmLabel: 'Desativar',
                        onConfirm: async () => {
                          await api.put(`/users/${user.id}`, { status: 'INACTIVE' });
                          await load();
                          onChanged();
                        },
                      })
                    }
                  >
                    Desativar acesso
                  </Button>
                ) : user.status === 'INACTIVE' ? (
                  <Button
                    variant="outline"
                    disabled={ocupado}
                    onClick={() =>
                      void acao(
                        () => api.put(`/users/${user.id}`, { status: 'ACTIVE' }),
                        'Usuário reativado.',
                      )
                    }
                  >
                    Reativar acesso
                  </Button>
                ) : null}
              </div>
            </div>

            {/*
              A marca de master é de papel Weloze, e é a única alteração que a
              pessoa pode fazer na própria conta: ela não muda o que se pode
              fazer, só o que se vê no relatório — então não há como alguém se
              trancar para fora com ela.
            */}
            {isWelozeRole(user.role) && user.status === 'ACTIVE' && (
              <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 p-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={user.isMaster}
                  disabled={ocupado}
                  onChange={(e) =>
                    void acao(
                      () => api.put(`/users/${user.id}`, { isMaster: e.target.checked }),
                      e.target.checked
                        ? 'Passa a ver a trilha dos relatórios.'
                        : 'Deixa de ver a trilha dos relatórios.',
                    )
                  }
                  className="mt-0.5 size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                <span>
                  <span className="font-medium text-slate-800">Usuário master</span>
                  <span className="mt-0.5 block text-xs text-slate-500">
                    Vê a trilha do relatório da competência — quem fechou, publicou e recalculou, e as
                    importações que a cobriram. Os demais usuários, inclusive administradores, veem o
                    relatório sem ela.
                  </span>
                </span>
              </label>
            )}

            {user.status === 'ACTIVE' && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={ocupado}
                  onClick={() =>
                    void acao(async () => {
                      setConvite(await api.post<UserInviteDto>(`/users/${user.id}/invite`));
                    }, 'Convite reemitido. O link anterior deixou de valer.')
                  }
                >
                  <Mail className="size-4" aria-hidden />
                  {user.pendingFirstAccess ? 'Reenviar convite' : 'Enviar link de nova senha'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={ocupado}
                  onClick={() =>
                    ask({
                      title: 'Encerrar as sessões',
                      body: (
                        <p>
                          Todos os dispositivos em que <strong>{user.name}</strong> está logado
                          precisarão entrar de novo. A senha não muda.
                        </p>
                      ),
                      confirmLabel: 'Encerrar sessões',
                      onConfirm: async () => {
                        await api.post(`/users/${user.id}/revoke-tokens`);
                        await load();
                      },
                    })
                  }
                >
                  <KeyRound className="size-4" aria-hidden />
                  Encerrar sessões
                </Button>
              </div>
            )}

            {convite && <InvitePanel invite={convite} />}

            <section className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 font-medium text-slate-900">
                  <Building2 className="size-4 text-slate-400" aria-hidden />
                  Lojas
                </h3>
                {!isWelozeRole(user.role) && user.status === 'ACTIVE' && !concedendo && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setNovasLojas([]);
                      setConcedendo(true);
                    }}
                  >
                    <Plus className="size-4" aria-hidden />
                    Conceder loja
                  </Button>
                )}
              </div>

              {isWelozeRole(user.role) ? (
                <p className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 text-sm text-slate-600">
                  Enxerga a carteira inteira. Papel Weloze não recebe concessão por loja — e não
                  existe flag “todas as lojas” a conceder.
                </p>
              ) : user.stores.length === 0 ? (
                <p className="rounded-lg border border-red-200 bg-red-50/50 p-3 text-sm text-red-800">
                  Este usuário não enxerga loja nenhuma. Ele entra no portal e não vê a própria
                  empresa, nem uma RNC.
                </p>
              ) : (
                <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {user.stores.map((loja) => (
                    <li
                      key={loja.storeId}
                      className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-slate-800">
                          {loja.supplierName}
                        </span>
                        <span className="block text-xs text-slate-500">
                          concedida em {formatDateTime(loja.grantedAt)}
                          {loja.grantedByName && ` por ${loja.grantedByName}`}
                        </span>
                      </span>
                      {loja.isResponsible && (
                        <Badge tone="brand">
                          <ShieldCheck className="size-3" aria-hidden />
                          responsável
                        </Badge>
                      )}
                      {!loja.monitored && <Badge tone="warning">não monitorada</Badge>}
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600">
                        {loja.protheusRef}
                      </span>
                      {/*
                        Sem responsável designado, a empresa inteira fica
                        bloqueada no portal e não tem como se destravar sozinha —
                        só o responsável aceita o Manual (RQ 271-00). É por isto
                        que o botão fica aqui, ao lado da concessão de acesso:
                        são os dois atos que a Weloze precisa fazer para a
                        empresa começar a usar.
                      */}
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={ocupado || user.status !== 'ACTIVE'}
                        onClick={() =>
                          void acao(
                            () =>
                              api.put(`/stores/${loja.storeId}/responsible`, {
                                userId: loja.isResponsible ? null : user.id,
                              }),
                            loja.isResponsible
                              ? 'Deixou de ser o responsável.'
                              : 'Designado responsável pela empresa.',
                          )
                        }
                      >
                        {loja.isResponsible ? 'Remover responsável' : 'Tornar responsável'}
                      </Button>
                      <button
                        type="button"
                        aria-label={`Revogar acesso a ${loja.supplierName}`}
                        disabled={ocupado}
                        className="rounded p-1 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                        onClick={() =>
                          ask({
                            title: 'Revogar acesso',
                            body: (
                              <p>
                                <strong>{user.name}</strong> deixa de enxergar{' '}
                                <strong>{loja.supplierName}</strong> no próximo clique dele. O
                                registro da concessão é preservado — a linha fica marcada como
                                revogada, não some.
                              </p>
                            ),
                            confirmLabel: 'Revogar',
                            onConfirm: async () => {
                              await api.delete(`/users/${user.id}/store-access/${loja.storeId}`);
                              await load();
                              onChanged();
                            },
                          })
                        }
                      >
                        <Trash2 className="size-4" aria-hidden />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {concedendo && (
                <div className="space-y-3 rounded-lg border border-slate-200 p-3">
                  <StorePicker
                    selected={novasLojas}
                    onChange={setNovasLojas}
                    excluded={user.stores.map((loja) => loja.storeId)}
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setConcedendo(false)}>
                      Cancelar
                    </Button>
                    <Button
                      size="sm"
                      loading={ocupado}
                      disabled={novasLojas.length === 0}
                      onClick={() =>
                        void acao(async () => {
                          // Uma chamada por loja: a rota concede uma de cada
                          // vez, e cada concessão é um ato próprio na trilha.
                          for (const storeId of novasLojas) {
                            await api.post(`/users/${user.id}/store-access`, { storeId });
                          }
                          setConcedendo(false);
                          setNovasLojas([]);
                        }, 'Acesso concedido.')
                      }
                    >
                      Conceder{' '}
                      {novasLojas.length > 0 &&
                        `${novasLojas.length} loja${novasLojas.length === 1 ? '' : 's'}`}
                    </Button>
                  </div>
                </div>
              )}
            </section>

            <div className="flex justify-end">
              <Button variant="outline" onClick={onClose}>
                Fechar
              </Button>
            </div>
          </div>
        )}
      </Modal>
      {dialog}
    </>
  );
}

function Linha({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-1.5 text-sm sm:border-0">
      <dt className="shrink-0 text-slate-500">{label}</dt>
      <dd className="text-right text-slate-800">{children}</dd>
    </div>
  );
}
